import React, { createContext, useContext, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { keyframes, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";

// Admin cards get their refresh from here (see TrustCenterAdmin): it waits
// for running saves, then refetches the Trust Center plus the named queries.
export const TrustCenterRefreshContext = createContext(null);

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Reloads one card's data in place, like the refresh button on AWS console
// panels, so nobody has to reload the whole page. The data already on screen
// stays while it loads. `queries` names extra queries the card reads (admin);
// `onRefresh` replaces the admin refresh (public page). `onDark`: for a dark
// background (the public page's banner).
const RefreshButton = ({ label, queries = [], onRefresh, size = 40, onDark = false }) => {
  const theme = useTheme();
  const adminRefresh = useContext(TrustCenterRefreshContext);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const run = onRefresh || (adminRefresh && (() => adminRefresh(queries)));
  if (!run) return null;

  const refresh = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await run();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip
      arrow
      title={
        failed
          ? "Could not refresh. Try again."
          : busy
            ? "Refreshing…"
            : "Refresh"
      }
    >
      {/* A disabled button fires no events; the span keeps the tooltip. */}
      <span>
        <IconButton
          aria-label={`Refresh ${label}`}
          aria-busy={busy}
          onClick={refresh}
          disabled={busy}
          sx={{
            width: size,
            height: size,
            flexShrink: 0,
            borderRadius: "8px",
            border: `1px solid ${
              failed
                ? theme.customColors.status.error.main
                : onDark
                  ? "rgba(255, 255, 255, 0.22)"
                  : theme.customColors.border.main
            }`,
            backgroundColor: onDark
              ? "rgba(255, 255, 255, 0.08)"
              : theme.customColors.background.main,
            color: failed
              ? theme.customColors.status.error.main
              : onDark
                ? "#FFFFFF"
                : theme.customColors.icons.primary,
            ...(onDark && {
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.16)" },
              "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.6)" },
            }),
          }}
        >
          <RefreshIcon
            sx={{
              fontSize: size >= 36 ? 20 : 16,
              animation: busy ? `${spin} 0.8s linear infinite` : "none",
            }}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default RefreshButton;
